import * as React from 'react'

import {
  QuantitySelector,
  Price,
  PriceProps,
} from '@thg-commerce/gravity-elements'
import { ProductOptions, Tags } from '@thg-commerce/gravity-patterns'
import { Option } from '@thg-commerce/gravity-patterns/ProductOptions/ProductOptions'
import { ProductImageProps } from '@thg-commerce/gravity-elements/Product/ProductImage/ProductImage'
import { QuantitySelectorProps } from '@thg-commerce/gravity-elements/QuantitySelector/QuantitySelector'

import {
  ModalTitle,
  StyledErrorMessage,
  ProductTitle,
  ModalContentWrapper,
  StyledProductImage,
  ContentContainer,
  PricingSection,
  AddToBasketButton,
  ViewInfoButton,
  AvailabilityMessage,
  StyledPageMessage,
  ProductTagsContainer,
} from './styles'

export interface QuickBuyModalContentProps {
  i18nText: {
    modalTitle: string
    rrpLabel: string
    saveLabel: string
    addToBasket: string
    viewMoreInformation: string
    missingProductMessage: string
    availabilityMessage?: string
    failedToAddToBasketError?: string
  }
  product?: {
    sku: string
    title: string
    image: Omit<ProductImageProps, 'width' | 'height'>
    tags?: string[]
    inStock: boolean
  }
  productOptions: {
    options: Option[]
    onOptionChange: (label: string, value: string) => void
    i18nText: {
      swatch: {
        unavailableText: string
        closeButtonText: string
      }
      dropdown: {
        unavailableText: string
      }
      wishlist: {
        unavailableText: string
      }
    }
    selectedOptions?: {
      [option: string]: string
    }
  }
  pricing?: PriceProps['pricing']
  links?: {
    productUrl: string
  }
  callbacks: {
    quantityChanged?: (quantity: number) => void
    addedToBasketClicked: (
      selected: { [option: string]: string },
      quantity: number,
    ) => void
    viewMoreInformationClicked?: (
      event: React.MouseEvent<Element, MouseEvent>,
    ) => void
  }
  quantitySelector: Omit<
    QuantitySelectorProps,
    'editable' | 'quantityChangedCallback'
  >
  hideQuantitySelector?: boolean
}

const INITIAL_QUANTITY = 1

export const QuickBuyModalContent = ({
  i18nText,
  product,
  productOptions,
  pricing,
  links,
  quantitySelector,
  hideQuantitySelector,
  callbacks,
}: QuickBuyModalContentProps) => {
  if (!product) {
    return (
      <StyledPageMessage
        flashMessageId="quickBuyModalMissingProduct"
        message={i18nText.missingProductMessage}
        type="error"
      />
    )
  }

  const [selectedQuantity, setSelectedQuantity] = React.useState<number>(
    INITIAL_QUANTITY,
  )

  const allowAddToBasket =
    productOptions.selectedOptions &&
    Object.keys(productOptions.selectedOptions)?.length ===
      productOptions.options.length &&
    selectedQuantity &&
    product.inStock

  return (
    <React.Fragment>
      <ModalTitle>{i18nText.modalTitle}</ModalTitle>
      {i18nText.failedToAddToBasketError && (
        <StyledErrorMessage
          error={i18nText.failedToAddToBasketError}
          id={`quick-buy-add-to-basket-error-${product.sku}`}
        />
      )}
      <ModalContentWrapper>
        <StyledProductImage
          {...product.image}
          width={['100%', '100%', 190, 191]}
          height={['', '', 190, 191]}
        />
        <ContentContainer>
          <ProductTitle
            reducedBottomMargin={product.tags?.length || 0 > 0 ? true : false}
          >
            {product.title}
          </ProductTitle>
          {product.tags && (product.tags?.length || 0) > 0 && (
            <ProductTagsContainer>
              <Tags tags={product.tags} />
            </ProductTagsContainer>
          )}
          <ProductOptions {...productOptions} />
          <PricingSection>
            {pricing && (
              <Price
                pricing={pricing}
                i18nText={{
                  saveLabel: i18nText.saveLabel,
                  rrpLabel: i18nText.rrpLabel,
                }}
              />
            )}
            {!hideQuantitySelector && (
              <QuantitySelector
                {...quantitySelector}
                quantityChangedCallback={(quantity) => {
                  if (quantity) {
                    setSelectedQuantity(quantity)
                  }
                }}
                editable={true}
                quantity={selectedQuantity}
              />
            )}
          </PricingSection>
          <AddToBasketButton
            emphasis="high"
            disabled={!allowAddToBasket}
            onClick={() => {
              if (
                allowAddToBasket &&
                selectedQuantity &&
                productOptions.selectedOptions
              ) {
                callbacks.addedToBasketClicked(
                  productOptions.selectedOptions,
                  selectedQuantity,
                )
              }
            }}
          >
            {i18nText.addToBasket}
          </AddToBasketButton>
          {i18nText.availabilityMessage && (
            <AvailabilityMessage>
              {i18nText.availabilityMessage}
            </AvailabilityMessage>
          )}
          {links && (
            <ViewInfoButton
              emphasis="medium"
              href={links.productUrl}
              renderedAs="a"
              onClick={
                callbacks?.viewMoreInformationClicked &&
                callbacks.viewMoreInformationClicked
              }
            >
              {i18nText.viewMoreInformation}
            </ViewInfoButton>
          )}
        </ContentContainer>
      </ModalContentWrapper>
    </React.Fragment>
  )
}
